import * as React from 'react';
import { Text } from '../../display';
import { getLabelColor, buildContainerClasses } from '../shared';

export type SwitchSize = 'sm' | 'md' | 'lg';

export interface SwitchProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: string;
  size?: SwitchSize;
  error?: boolean;
  helperText?: string;
}

const sizeClasses: Record<
  SwitchSize,
  { track: string; thumb: string; translate: string }
> = {
  sm: {
    track: 'w-7 h-4',
    thumb: 'w-3 h-3',
    translate: 'translate-x-3',
  },
  md: {
    track: 'w-9 h-5',
    thumb: 'w-4 h-4',
    translate: 'translate-x-4',
  },
  lg: {
    track: 'w-11 h-6',
    thumb: 'w-5 h-5',
    translate: 'translate-x-5',
  },
};

interface SwitchState {
  disabled: boolean;
  error: boolean;
}

const trackBaseClasses = [
  'relative',
  'inline-flex',
  'items-center',
  'shrink-0',
  'rounded-full',
  'p-0.5',
  'transition-colors',
  'duration-150',
  'ease-out',
];

function getDefaultTrackClasses(): string[] {
  return ['bg-secondary-300', 'peer-checked:bg-primary-700'];
}

function getErrorTrackClasses(): string[] {
  return ['bg-error-200', 'peer-checked:bg-error-700'];
}

function getDisabledTrackClasses(): string[] {
  return [
    'bg-secondary-200',
    'peer-checked:bg-primary-300',
    'opacity-70',
  ];
}

function getFocusClasses(): string[] {
  return [
    'peer-focus-visible:ring-2',
    'peer-focus-visible:ring-primary-200',
    'peer-focus-visible:ring-offset-1',
  ];
}

function buildTrackClasses(trackSize: string, state: SwitchState): string {
  const classes: string[] = [trackSize, ...trackBaseClasses];

  if (state.disabled) {
    classes.push(...getDisabledTrackClasses());
    return classes.join(' ');
  }

  classes.push(...getFocusClasses());

  if (state.error) {
    classes.push(...getErrorTrackClasses());
  } else {
    classes.push(...getDefaultTrackClasses());
  }

  return classes.join(' ');
}

function buildThumbClasses(
  thumbSize: string,
  translate: string,
  effectiveChecked: boolean,
): string {
  return [
    thumbSize,
    'inline-block',
    'rounded-full',
    'bg-white',
    'shadow-sm',
    'transform',
    'transition-transform',
    'duration-150',
    'ease-out',
    effectiveChecked ? translate : 'translate-x-0',
  ].join(' ');
}

const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
  (
    {
      label,
      size = 'md',
      error = false,
      helperText,
      className = '',
      disabled = false,
      id: providedId,
      checked,
      defaultChecked,
      onChange,
      'aria-describedby': ariaDescribedBy,
      ...props
    },
    ref,
  ) => {
    const internalRef = React.useRef<HTMLInputElement>(null);
    React.useImperativeHandle(ref, () => internalRef.current as HTMLInputElement);

    // Track the input's checked state in React so the thumb can slide.
    // `peer-checked:` only matches siblings of the peer input, but the
    // thumb is nested inside the track — so we drive its translate
    // class from state instead. Track colour still uses `peer-checked:`
    // because the track itself IS a sibling of the input.
    const isControlled = checked !== undefined;
    const [internalChecked, setInternalChecked] = React.useState<boolean>(
      defaultChecked ?? false,
    );
    const effectiveChecked = isControlled ? !!checked : internalChecked;

    const handleChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!isControlled) setInternalChecked(e.target.checked);
        onChange?.(e);
      },
      [isControlled, onChange],
    );

    const reactId = React.useId();
    const switchId = providedId || `switch-${reactId}`;
    const helperId = `switch-helper-${reactId}`;

    const messageId = helperText ? helperId : undefined;
    const computedAriaDescribedBy =
      [ariaDescribedBy, messageId].filter(Boolean).join(' ') || undefined;

    const { track: trackSize, thumb: thumbSize, translate } = sizeClasses[size];
    const state: SwitchState = { disabled, error };

    const trackClasses = buildTrackClasses(trackSize, state);
    const thumbClasses = buildThumbClasses(thumbSize, translate, effectiveChecked);
    const containerClasses = buildContainerClasses(disabled, className);
    const labelColor = getLabelColor(disabled, error);
    const labelCursor = disabled ? 'cursor-not-allowed' : 'cursor-pointer';
    const helperColor = error ? 'error' : 'muted';

    return (
      <div className="w-fit">
        <label className={containerClasses}>
          <input
            ref={internalRef}
            type="checkbox"
            role="switch"
            id={switchId}
            disabled={disabled}
            checked={isControlled ? checked : undefined}
            defaultChecked={isControlled ? undefined : defaultChecked}
            onChange={handleChange}
            aria-invalid={error}
            aria-describedby={computedAriaDescribedBy}
            className="peer sr-only"
            {...props}
          />

          <span className={trackClasses} aria-hidden="true">
            <span className={thumbClasses} />
          </span>

          {label && (
            <Text as="span" size="base" color={labelColor} className={labelCursor}>
              {label}
            </Text>
          )}
        </label>

        {helperText && (
          <Text id={messageId} size="xs" color={helperColor} className="mt-1 ml-[26px]">
            {helperText}
          </Text>
        )}
      </div>
    );
  },
);

Switch.displayName = 'Switch';

export { Switch };
